import type { ReactElement } from "react";
import { Group, Line, Rect, Text } from "react-konva";
import type { FitInfo } from "../../lib/useFitToRoom";
import type {
  CollaboratorState,
  PresenceCanvasView,
} from "../../lib/sharedRoomTypes";
import type { Furniture, Opening, Vec2, WallMount } from "../../lib/types";
import { resolveFurniture } from "../../lib/furniture/resolve";
import { chromeScale } from "../../lib/canvasLabels";

/**
 * Konva overlay that draws a colored selection halo + name pill on every
 * item another collaborator has selected. Renders nothing when no one
 * else has anything selected in the current view.
 *
 * View-aware so the same component can be reused across the design and
 * build canvases (both use `useFitToRoom`). The wall view has its own
 * dedicated overlay (see `WallCollaboratorOverlay`) because its
 * coordinate system is different.
 */

interface Props {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  furniture: Furniture[];
  mounts: WallMount[];
  openings: Opening[];
  roomPoints: Vec2[];
  fit: FitInfo;
  view: PresenceCanvasView;
  activeWallIndex: number | null;
  /** Stage zoom, so halos and name pills keep one on-screen size. */
  stageScale?: number;
}

const NAME_BG_OPACITY = 0.95;

export function CollaboratorSelections({
  collaborators,
  furniture,
  mounts,
  openings,
  roomPoints,
  fit,
  view,
  activeWallIndex,
  stageScale = 1,
}: Props) {
  if (collaborators.size === 0) return null;

  const k = chromeScale(stageScale);
  const items: ReactElement[] = [];

  for (const collab of collaborators.values()) {
    if (!collab.selection) continue;

    if (view === "design") {
      if (collab.selection.furnitureId) {
        const f = furniture.find(
          (x) => x.id === collab.selection!.furnitureId
        );
        if (f) {
          items.push(
            <FurnitureSelectionHalo
              key={`fs-${collab.uid}-${f.id}`}
              collab={collab}
              furniture={f}
              fit={fit}
              k={k}
            />
          );
        }
      }
    }

    if (view === "wall" && activeWallIndex !== null) {
      // The wall view uses dedicated overlays — leave selection blank
      // here so we don't double-render. Mounts/openings args are still
      // accepted so the prop shape stays uniform across views.
      void mounts;
      void openings;
    }

    if (view === "build") {
      if (collab.selection.roomVertexIndex !== null) {
        const v = roomPoints[collab.selection.roomVertexIndex];
        if (v) {
          items.push(
            <VertexHalo
              key={`vs-${collab.uid}-${collab.selection.roomVertexIndex}`}
              collab={collab}
              vertex={v}
              fit={fit}
              k={k}
            />
          );
        }
      }
      if (collab.selection.roomWallIndex !== null) {
        const a = roomPoints[collab.selection.roomWallIndex];
        const b =
          roomPoints[
            (collab.selection.roomWallIndex + 1) % roomPoints.length
          ];
        if (a && b) {
          items.push(
            <WallHalo
              key={`ws-${collab.uid}-${collab.selection.roomWallIndex}`}
              collab={collab}
              a={a}
              b={b}
              fit={fit}
              k={k}
            />
          );
        }
      }
    }
  }

  return <>{items}</>;
}

function NamePill({
  x,
  y,
  name,
  color,
  k,
}: {
  x: number;
  y: number;
  name: string;
  color: string;
  /** Counter-scale for the Stage zoom. */
  k: number;
}) {
  const charW = 6.5;
  const padX = 6;
  const labelW = name.length * charW + padX * 2;
  return (
    <Group x={x} y={y} scaleX={k} scaleY={k} listening={false}>
      <Rect
        x={0}
        y={0}
        width={labelW}
        height={18}
        fill={color}
        opacity={NAME_BG_OPACITY}
        cornerRadius={9}
      />
      <Text
        x={padX}
        y={3}
        text={name}
        fontSize={11}
        fontStyle="600"
        fontFamily="Inter, sans-serif"
        fill="#ffffff"
      />
    </Group>
  );
}

function FurnitureSelectionHalo({
  collab,
  furniture,
  fit,
  k,
}: {
  collab: CollaboratorState;
  furniture: Furniture;
  fit: FitInfo;
  k: number;
}) {
  const center = fit.pointToPx({ x: furniture.x, y: furniture.y });
  const w = fit.inToPx(furniture.width);
  const d = fit.inToPx(furniture.depth);
  // Halo hugs the true footprint (L / U sectionals, round tables).
  const footprint = resolveFurniture(furniture).footprint;
  const flat: number[] = [];
  for (const p of footprint) flat.push(p.x * fit.scale, p.y * fit.scale);
  return (
    <Group
      x={center.x}
      y={center.y}
      rotation={furniture.rotation}
      listening={false}
    >
      <Line
        points={flat}
        closed
        stroke={collab.color}
        strokeWidth={2.5}
        strokeScaleEnabled={false}
        lineJoin="round"
      />
      {/* Counter-rotate the name pill so it stays upright when the
          piece is rotated. */}
      <Group rotation={-furniture.rotation} x={-w / 2 - 2 * k} y={-d / 2 - 22 * k}>
        <NamePill x={0} y={0} name={collab.displayName} color={collab.color} k={k} />
      </Group>
    </Group>
  );
}

function VertexHalo({
  collab,
  vertex,
  fit,
  k,
}: {
  collab: CollaboratorState;
  vertex: Vec2;
  fit: FitInfo;
  k: number;
}) {
  const p = fit.pointToPx(vertex);
  return (
    <Group x={p.x} y={p.y} listening={false}>
      <Rect
        x={-9}
        y={-9}
        width={18}
        height={18}
        stroke={collab.color}
        strokeWidth={2.5}
        cornerRadius={9}
      />
      <NamePill x={12 * k} y={-22 * k} name={collab.displayName} color={collab.color} k={k} />
    </Group>
  );
}

function WallHalo({
  collab,
  a,
  b,
  fit,
  k,
}: {
  collab: CollaboratorState;
  a: Vec2;
  b: Vec2;
  fit: FitInfo;
  k: number;
}) {
  const aPx = fit.pointToPx(a);
  const bPx = fit.pointToPx(b);
  const midX = (aPx.x + bPx.x) / 2;
  const midY = (aPx.y + bPx.y) / 2;
  return (
    <Group listening={false}>
      <Line
        points={[aPx.x, aPx.y, bPx.x, bPx.y]}
        stroke={collab.color}
        strokeWidth={5}
        lineCap="round"
        opacity={0.85}
      />
      <NamePill
        x={midX + 8 * k}
        y={midY - 22 * k}
        name={collab.displayName}
        color={collab.color}
        k={k}
      />
    </Group>
  );
}
